/* Piece tables. These ARE the rules for poles and movement; the rulebook text is generated from them (D7). */
import type { Pole, ShapeId, Side } from './types.ts';

/** Directions in a piece's own frame. F = toward the opponent, R = the owner's right hand. */
export const FRAMES = ['F', 'FR', 'R', 'BR', 'B', 'BL', 'L', 'FL'] as const;
export type Frame = (typeof FRAMES)[number];
export const F = 0, FR = 1, R = 2, BR = 3, B = 4, BL = 5, L = 6, FL = 7;

/** Board vector of each frame direction for a White piece (White faces +row, its right hand is +col). Black is rotated 180 degrees (D1). */
export const FRAME_VEC: ReadonlyArray<readonly [number, number]> = [
  [1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1],
];
/** The eight board directions, same order as FRAME_VEC. */
export const DIR8 = FRAME_VEC;

const VEC_TO_FRAME = new Int8Array(9).fill(-1);
FRAME_VEC.forEach(([dr, dc], i) => { VEC_TO_FRAME[(dr + 1) * 3 + (dc + 1)] = i; });

/** Frame index a piece of `side` uses for board direction (dr,dc). */
export function frameOf(side: Side, dr: number, dc: number): number {
  if (side === 1) { dr = -dr; dc = -dc; }
  return VEC_TO_FRAME[(dr + 1) * 3 + (dc + 1)];
}
/** Board vector for a frame direction of a piece of `side`. */
export function vecOf(side: Side, frame: number): [number, number] {
  const [dr, dc] = FRAME_VEC[frame];
  return side === 1 ? [-dr, -dc] : [dr, dc];
}

export interface ShapeDef {
  id: ShapeId;
  name: string;
  /** Working character name from the review. */
  codename: string;
  /** Frame directions this piece may move in (1 or 2 cells). */
  move: readonly number[];
  /** edge[frame] = true when the piece presents a true edge in that direction, false = a corner. */
  edge: readonly boolean[];
  /** pole[frame] = pole presented in that direction. Corners: the pole of the two adjacent edges when they agree, else neutral; the triangle's flanks are the documented exception (they present +). */
  pole: readonly Pole[];
}

function def(id: ShapeId, name: string, codename: string, move: Frame[], edges: Partial<Record<Frame, Pole>>, corners: Partial<Record<Frame, Pole>>): ShapeDef {
  const edge: boolean[] = [], pole: Pole[] = [];
  FRAMES.forEach((f, i) => {
    const e = edges[f];
    if (e !== undefined) { edge[i] = true; pole[i] = e; }
    else { edge[i] = false; pole[i] = corners[f] ?? 0; }
  });
  return { id, name, codename, move: move.map(f => FRAMES.indexOf(f)), edge, pole };
}

export const SHAPES: readonly ShapeDef[] = [
  def(0, 'triangle', 'Wedge', ['FL', 'FR', 'B'],
    { FL: 1, FR: 1, B: -1 },
    { F: 1, L: 1, R: 1, BL: 0, BR: 0 }),
  def(1, 'square', 'Block', ['F', 'B', 'L', 'R'],
    { F: 1, R: 1, B: -1, L: -1 },
    { FR: 1, BL: -1, FL: 0, BR: 0 }),
  def(2, 'rhombus', 'Kite', ['FL', 'FR', 'BL', 'BR'],
    { FL: 1, FR: 1, BL: -1, BR: -1 },
    { F: 1, B: -1, L: 0, R: 0 }),
  def(3, 'octagon', 'Core', ['F', 'FR', 'R', 'BR', 'B', 'BL', 'L', 'FL'],
    { F: 1, FL: 1, FR: 1, R: 1, L: -1, BL: -1, BR: -1, B: -1 },
    {}),
];

/** Pole a piece presents toward board direction (dr,dc). */
export function poleToward(shape: ShapeId, side: Side, dr: number, dc: number): Pole {
  return SHAPES[shape].pole[frameOf(side, dr, dc)];
}
/** Does the piece present a true edge toward board direction (dr,dc)? */
export function edgeToward(shape: ShapeId, side: Side, dr: number, dc: number): boolean {
  return SHAPES[shape].edge[frameOf(side, dr, dc)];
}
